import React from "react";

import {
  ANIMATION_DURATION,
  SUCCESS_TIMEOUT,
  add,
  remove as removeAction,
  flagForRemoval,
  makeNotification
} from "Reducers/notifications";
import type {
  Notification,
  NotificationId,
  Priorities
} from "Reducers/notifications";

import type { AppDispatch } from "Store/configureStore";

export const remove = (notificationId: NotificationId, dispatch: AppDispatch) =>
  new Promise<NotificationId>(fulfill => {
    dispatch(flagForRemoval(notificationId));
    window.setTimeout(() => {
      dispatch(removeAction(notificationId));
      fulfill(notificationId);
    }, ANIMATION_DURATION);
  });

export const notifyWithPriorityHandler = (dispatch: AppDispatch) => {
  const notifyWithPriority =
    (priority: Priorities) =>
    (
      partialNotification: Partial<Notification> | string,
      description?: React.ReactNode
    ) => {
      if (typeof partialNotification === "string") {
        partialNotification = {
          title: partialNotification
        };
      }

      if (description) {
        partialNotification.description = description;
      }

      // Auto-hide information and success messages. This can be overwritten
      // by the partialNotification if needed
      if (
        priority === "information" ||
        priority === "success" ||
        priority === "loading"
      ) {
        partialNotification.timeout =
          partialNotification.timeout || SUCCESS_TIMEOUT;
      }

      const notification = makeNotification({
        priority: priority,
        ...partialNotification
      });

      // Sets up a timer for auto destruction. There's one already included on
      // the radix implementation but is a bit hard to manage if we want to
      // decouple the notifications from the DOM.
      if (notification.timeout) {
        window.setTimeout(() => {
          remove(notification.id, dispatch);
        }, notification.timeout - ANIMATION_DURATION);
      }

      dispatch(add(notification));

      return notification;
    };

  return {
    warning: notifyWithPriority("warning"),
    information: notifyWithPriority("information"),
    success: notifyWithPriority("success"),
    error: notifyWithPriority("error"),
    loading: notifyWithPriority("loading"),
    remove: notificationId => remove(notificationId, dispatch)
  };
};
